import { useTab } from '@/hooks/useTab';
import Box from '@mui/material/Box';
import dynamic from 'next/dynamic';
import { NextRouter, useRouter } from 'next/router';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { getFolderPlan } from '../../../apis/tms/folder.api';
import { checkIsUserJoined } from '../../../common/checkRoleUser';
import { deepClone } from '../../../common/other';
import { GROUP_PLAN } from '../../../constant/QueryKeyName';
import { GROUP_TABS } from '../../../constant/group';
import { ROLE_ACCESS, TRoleAccess, USER_PLAN } from '../../../constant/storage';
import useOpenManageRequestJoinGroup from '../../../hooks/useOpenManageRequestJoinGroup';
import { FolderStatusEnum } from '../../../interfaces/training.interface';
import TabPanel from '../../tabs/TabPanel';
import TabsCustomized from '../../tabs/TabsCustomized';
import LockGroupContentHOC from './LockGroupScreen';

const DynamicCourseTab = dynamic(() => import('../../tabs/CourseTab'), {
  ssr: false,
});
const DynamicFeedTab = dynamic(() => import('../../tabs/FeedTab'), {
  ssr: false,
});
const DynamicDocumentTab = dynamic(() => import('../../tabs/DocumentTab'), {
  ssr: false,
});
const DynamicGroupSettingsTab = dynamic(
  () => import('../../tabs/GroupSettingsTab'),
  {
    ssr: false,
  }
);

interface ITabsInGroupDetail {
  folder: any;
  folderId: string;
  mineRole: TRoleAccess | undefined;
  folderStatus: FolderStatusEnum;
}

const TabsInGroupDetail = ({
  folder,
  folderId,
  mineRole,
  folderStatus,
}: ITabsInGroupDetail) => {
  const { data: groupPlan } = useQuery<any, Error, string>(
    [GROUP_PLAN, folderId],
    () => getFolderPlan(folderId) as any,
    {
      refetchOnMount: true,
      enabled: !!folderId,
      retry(_failureCount, _error: any) {
        return false;
      },
    }
  );

  const router = useRouter();
  const [tabList, setTabList] = useState<{ key: string; label: string }[]>([
    GROUP_TABS.FEED,
    GROUP_TABS.DOCUMENT,
    GROUP_TABS.SETTINGS,
  ]);
  const { currentTab, setCurrentTab, handleChangeTab } = useTab();
  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    handleChangeTab(_event, newValue);
    window.postMessage(
      { message: 'stateChange', data: tabList[newValue].key },
      window.location.origin
    );
  };
  const tabPanel: {
    /** key of this object must be keyof tabList, check useEffect right below */
    [key: string]: {
      component: React.ReactElement;
      /** This mean for group public, do we need to lock this tab */
      publicLock: boolean;
      /** This mean for group private, do we need to lock this tab */
      privateLock: boolean;
    };
  } = {
    feed: {
      component: <DynamicFeedTab />,
      publicLock: false,
      privateLock: true,
    },
    course: {
      component: (
        <DynamicCourseTab
          group={folder}
          folderId={folderId as string}
          mineRole={mineRole}
        />
      ),
      publicLock: false,
      privateLock: false,
    },
    document: {
      component: (
        <DynamicDocumentTab
          group={folder}
          folderId={folderId as string}
          mineRole={mineRole}
        />
      ),
      publicLock: true,
      privateLock: true,
    },
    settings: {
      component: (
        <DynamicGroupSettingsTab
          folderData={folder}
          folderId={folderId as string}
          mineRole={mineRole}
        />
      ),
      publicLock: false,
      privateLock: false,
    },
  };

  const conditionShowLockGroupContent =
    !checkIsUserJoined(mineRole as ROLE_ACCESS) &&
    tabPanel[tabList[currentTab]?.key]?.[
      folderStatus === FolderStatusEnum.Public ? 'publicLock' : 'privateLock'
    ];

  const descriptionContent = (
    <div className="flex flex-col items-stretch justify-center gap-1">
      <h5 className="text-center text-base font-bold leading-[140%] tracking-[-0.3px] text-[#242424]">
        {folderStatus === FolderStatusEnum.Private
          ? 'Nhóm riêng tư'
          : 'Khóa nội dung'}
      </h5>
      <p className="text-center text-sm font-medium leading-[150%] text-[#242424]">
        {folderStatus === FolderStatusEnum.Private
          ? 'Đây là nhóm riêng tư. Hãy trở thành thành viên nhóm để truy cập những nội dung độc quyền.'
          : 'Nội dung dành riêng cho thành viên của nhóm, bạn vui lòng tham gia nhóm để truy cập nội dung trên'}
      </p>
    </div>
  );

  useEffect(() => {
    const handleSetCurrentTab = (
      tabList: { key: string; label: string }[],
      router: NextRouter
    ) => {
      const { showMemberTab, joinRequestTab } = router.query;

      const showMemberTabCondition =
        showMemberTab === '1' && checkIsUserJoined(mineRole as ROLE_ACCESS);

      const showRequestTabCondition =
        joinRequestTab === 'true' && mineRole === ROLE_ACCESS.Owner;

      if (showMemberTabCondition || showRequestTabCondition)
        setCurrentTab(tabList.findIndex((item) => item.key === 'settings'));
    };

    if (mineRole && groupPlan && router.isReady) {
      const isThisGroupHaveCourseTab =
        groupPlan === USER_PLAN.Business ||
        groupPlan === USER_PLAN.Enterprise ||
        groupPlan === USER_PLAN.Verified;

      setTabList((prev) => {
        const clone = deepClone(prev);
        if (
          !clone.find((item) => item.key === GROUP_TABS.COURSE.key) &&
          isThisGroupHaveCourseTab
        ) {
          clone.splice(1, 0, GROUP_TABS.COURSE);
        }
        handleSetCurrentTab(clone, router);
        return clone;
      });
    }
  }, [mineRole, groupPlan, router]);

  useOpenManageRequestJoinGroup(() =>
    setCurrentTab(tabList.findIndex((item) => item.key === 'settings'))
  );

  return (
    <>
      <div className="sticky top-[54px] z-[10] -mx-3 border-b border-Other/Outlinedborder-stroke bg-white px-5 md:top-[50px] md:mx-0">
        <TabsCustomized
          tabList={tabList.map((tab) => tab.label)}
          value={currentTab}
          handleChange={handleChange}
        />
      </div>
      <LockGroupContentHOC
        description={descriptionContent}
        conditionShowPrivateContent={conditionShowLockGroupContent}
      >
        <Box sx={{ width: '100%' }}>
          {tabList.map((item, index) => (
            <TabPanel key={index} index={index} value={currentTab}>
              {tabPanel[item.key as keyof typeof tabPanel].component}
            </TabPanel>
          ))}
        </Box>
      </LockGroupContentHOC>
    </>
  );
};

export default TabsInGroupDetail;
